(() => {
  if (!customElements.get('bumpino-product-tabs')) {
    class BumpinoProductTabs extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        const tabs = [...this.querySelectorAll('[role="tab"]')];
        const activate = (tab, focus = false) => {
          tabs.forEach(item => {
            const active = item === tab;
            item.setAttribute('aria-selected', String(active));
            item.tabIndex = active ? 0 : -1;
            document.getElementById(item.getAttribute('aria-controls')).hidden = !active;
          });
          if (focus) tab.focus();
        };
        tabs.forEach(tab => {
          tab.addEventListener('click', () => activate(tab));
          tab.addEventListener('keydown', event => {
            const index = tabs.indexOf(tab);
            const rtl = getComputedStyle(this).direction === 'rtl';
            let next;
            if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
            if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            activate(tabs[(next + tabs.length) % tabs.length], true);
          });
        });
        activate(tabs[0]);
        this.setAttribute('data-ready', '');
      }
    }
    customElements.define('bumpino-product-tabs', BumpinoProductTabs);
  }
  if (!customElements.get('bumpino-review-gallery')) {
    class BumpinoReviewGallery extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        const rail = this.querySelector('[data-review-rail]');
        const group = this.querySelector('[data-review-group]');
        const dialog = this.querySelector('[data-review-dialog]');
        const zoom = this.querySelector('[data-review-zoom]');
        const close = this.querySelector('[data-review-close]');
        if (!group.children.length) return;
        const clone = () => {
          const copy = group.cloneNode(true);
          copy.removeAttribute('data-review-group');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('button').forEach(button => { button.tabIndex = -1; button.removeAttribute('data-shopify-editor-block'); });
          return copy;
        };
        rail.prepend(clone());
        rail.append(clone());
        const motion = matchMedia('(prefers-reduced-motion: reduce)');
        let drag = null;
        let hovered = false;
        let focused = false;
        let visible = false;
        let last = 0;
        let motionCarry = 0;
        let resumeAt = 0;
        let suppressClickUntil = 0;
        let openedBy = null;
        let width = 0;
        let initialized = false;
        const idle = () => { resumeAt = performance.now() + 5000; };
        const normalize = () => {
          if (!width || drag || focused) return;
          if (rail.scrollLeft >= width * 2) rail.scrollLeft -= width;
          if (rail.scrollLeft < width) rail.scrollLeft += width;
        };
        const size = () => {
          const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
          const nextWidth = group.getBoundingClientRect().width + gap;
          if (!nextWidth) return;
          if (!initialized) { rail.scrollLeft = nextWidth; initialized = true; }
          else if (width && nextWidth !== width) rail.scrollLeft = nextWidth + (rail.scrollLeft % width);
          width = nextWidth;
        };
        this.resizeObserver = new ResizeObserver(size);
        this.resizeObserver.observe(group);
        this.intersectionObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, { threshold: .05 });
        this.intersectionObserver.observe(this);
        size();
        const animate = time => {
          const elapsed = last ? Math.min(time - last, 50) : 0;
          last = time;
          if (visible && !document.hidden && !motion.matches && !hovered && !focused && !drag && !dialog.open && time >= resumeAt) {
            normalize();
            motionCarry += elapsed * .025;
            const distance = Math.floor(motionCarry);
            if (distance) {
              rail.scrollLeft += distance;
              motionCarry -= distance;
            }
          }
          this.frame = requestAnimationFrame(animate);
        };
        rail.addEventListener('wheel', idle, { passive: true });
        rail.addEventListener('touchstart', idle, { passive: true });
        rail.addEventListener('touchmove', idle, { passive: true });
        rail.addEventListener('touchend', idle, { passive: true });
        rail.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') hovered = true; });
        rail.addEventListener('pointerleave', () => { hovered = false; idle(); });
        rail.addEventListener('focusin', () => { focused = true; });
        rail.addEventListener('focusout', event => { focused = rail.contains(event.relatedTarget); idle(); });
        rail.addEventListener('pointerdown', event => {
          idle();
          if (event.pointerType === 'mouse' && event.button === 0) drag = { id: event.pointerId, startX: event.clientX, lastX: event.clientX, moved: false };
        });
        rail.addEventListener('pointermove', event => {
          if (!drag) return;
          if (Math.abs(event.clientX - drag.startX) > 5) drag.moved = true;
          if (drag.moved) {
            rail.setPointerCapture(event.pointerId);
            rail.classList.add('is-dragging');
            rail.scrollLeft += drag.lastX - event.clientX;
          }
          drag.lastX = event.clientX;
        });
        const finishDrag = () => {
          if (drag?.moved) suppressClickUntil = performance.now() + 400;
          if (drag && rail.hasPointerCapture(drag.id)) rail.releasePointerCapture(drag.id);
          drag = null;
          rail.classList.remove('is-dragging');
          idle();
        };
        rail.addEventListener('pointerup', finishDrag);
        rail.addEventListener('pointercancel', finishDrag);
        rail.addEventListener('dragstart', event => event.preventDefault());
        rail.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          idle();
          rail.scrollBy({ left: rail.clientWidth * .75 * (event.key === 'ArrowRight' ? 1 : -1), behavior: motion.matches ? 'auto' : 'smooth' });
        });
        rail.addEventListener('click', event => {
          const button = event.target.closest('[data-review-image]');
          if (!button || performance.now() < suppressClickUntil) return;
          const image = button.querySelector('img');
          openedBy = button;
          zoom.src = image.src.split('&width=')[0].split('?width=')[0];
          zoom.style.aspectRatio = `${image.width} / ${image.height}`;
          dialog.showModal();
          close.focus();
        });
        close.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
        dialog.addEventListener('close', () => { zoom.removeAttribute('src'); openedBy?.focus({ preventScroll: true }); idle(); });
        this.frame = requestAnimationFrame(animate);
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
      }
    }
    customElements.define('bumpino-review-gallery', BumpinoReviewGallery);
  }
})();
